import React, { useEffect, useState } from "react";
import {
  AppBar as MuiAppBar,
  Box,
  CssBaseline,
  Divider,
  Drawer as MuiDrawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Link, Outlet, useNavigate } from "react-router-dom";
import ProfileMenu from "../ProfileMenu";
import apiClient from "../../utils/apiClient";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from 'react-redux';
import FallbackImage from "../FallbackImage";
import { pageRoute, siteData } from "../../utils/common";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import ListAltIcon from "@mui/icons-material/ListAlt";
import GroupIcon from "@mui/icons-material/Group"; // ✅ For User Management
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import EmailVerificationNotice from "../EmailVerificationNotice";
import BusinessIcon from "@mui/icons-material/Business";
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ReviewsIcon from '@mui/icons-material/Reviews';
import ReplayIcon from '@mui/icons-material/Replay';
import StoreIcon from "@mui/icons-material/Store";
import DashboardIcon from "@mui/icons-material/Dashboard";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";

const drawerWidth = 240;

// ✅ Drawer styles
const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== "open" })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    ...(open && {
      ...openedMixin(theme),
      "& .MuiDrawer-paper": openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      "& .MuiDrawer-paper": closedMixin(theme),
    }),
  })
);

export default function ResponsiveMiniDrawer() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [open, setOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerOpen = () => setOpen(true);
  const handleDrawerClose = () => setOpen(false);
  const handleMobileToggle = () => setMobileOpen(!mobileOpen);
  const logo = useSelector((state) => state.logo);
  const user = useSelector((state) => state.user);
  const dispatch = useDispatch()
  const getProfile = async () => {
    try {
      const res = await apiClient.get("/api/Vendor/get");
      dispatch({ key: 'user', value: res.data.data, type: 'SET_VALUE' })

    } catch (err) {
      if (err.response) {
        toast.error(err.response.data.message || "Somthing Wrong");
      } else {
        toast.error("Network error. Please try again.");
      }
    }
  }

  useEffect(() => {
    getProfile()
  }, [])

  const navigate = useNavigate()

  const pageChange = (url) => {
    navigate(url)
  }

  const drawerContent = (
    <>
      <DrawerHeader>
        {!isMobile && (
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === "rtl" ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        )}
      </DrawerHeader>
      <Divider />
      <List>
        <ListItem
          disablePadding
          sx={{ display: "block" }}
          onClick={() => pageChange(pageRoute.dashboard)}
        >
          <ListItemButton>
            <ListItemIcon>
              <DashboardIcon />
            </ListItemIcon>

            <ListItemText
              primary="Dashboard"
              sx={{ opacity: open ? 1 : 0 }}
            />
          </ListItemButton>
        </ListItem>
        <ListItem
          disablePadding
          sx={{ display: "block" }}
          onClick={() => pageChange(pageRoute.vendorEarning)}
        >
          <ListItemButton>
            <ListItemIcon>
              <CurrencyRupeeIcon />
            </ListItemIcon>

            <ListItemText
              primary="Vendor Earning"
              sx={{ opacity: open ? 1 : 0 }}
            />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding sx={{ display: "block" }} onClick={() => pageChange(pageRoute.vendorBusinessDetails)}>
          <ListItemButton>
            <ListItemIcon>
              <BusinessIcon />
            </ListItemIcon>
            <ListItemText primary="Business Details" sx={{ opacity: open ? 1 : 0 }} />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding sx={{ display: "block" }} onClick={() => pageChange(pageRoute.payoutDetails)}>
          <ListItemButton>
            <ListItemIcon>
              <AccountBalanceIcon />
            </ListItemIcon>
            <ListItemText primary="Payout Details" sx={{ opacity: open ? 1 : 0 }} />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding sx={{ display: "block" }} onClick={() => pageChange(pageRoute.brandManagement)}>
          <ListItemButton>
            <ListItemIcon>
              <StoreIcon />
            </ListItemIcon>
            <ListItemText primary="Brand Management" sx={{ opacity: open ? 1 : 0 }} />
          </ListItemButton>
        </ListItem>
        {/* Add Product */}
        <ListItem disablePadding sx={{ display: "block" }} onClick={() => pageChange(pageRoute.productAdd)}>
          <ListItemButton>
            <ListItemIcon>
              <AddShoppingCartIcon />
            </ListItemIcon>
            <ListItemText primary="Add Product" sx={{ opacity: open ? 1 : 0 }} />
          </ListItemButton>
        </ListItem>

        {/* Product List */}
        <ListItem disablePadding sx={{ display: "block" }} onClick={() => pageChange(pageRoute.productList)}>
          <ListItemButton>
            <ListItemIcon>
              <ListAltIcon />
            </ListItemIcon>
            <ListItemText primary="Product List" sx={{ opacity: open ? 1 : 0 }} />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding sx={{ display: "block" }} onClick={() => pageChange(pageRoute.allReview)}>
          <ListItemButton>
            <ListItemIcon>
              <ReviewsIcon />
            </ListItemIcon>
            <ListItemText primary="All Review" sx={{ opacity: open ? 1 : 0 }} />
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding sx={{ display: "block" }} onClick={() => pageChange(pageRoute.orderList)}>
          <ListItemButton>
            <ListItemIcon>
              <ReceiptLongIcon />
            </ListItemIcon>
            <ListItemText primary="Order List" sx={{ opacity: open ? 1 : 0 }} />
          </ListItemButton>
        </ListItem>
        {/* <ListItem disablePadding sx={{ display: "block" }} onClick={() => pageChange(pageRoute.returnList)}>
          <ListItemButton>
            <ListItemIcon>
              <ReplayIcon />
            </ListItemIcon>
            <ListItemText primary="Return List" sx={{ opacity: open ? 1 : 0 }} />
          </ListItemButton>
        </ListItem> */}
      </List>
    </>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />

      <AppBar position="fixed" open={open && !isMobile}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={isMobile ? handleMobileToggle : handleDrawerOpen}
            sx={{ mr: 2, ...(open && !isMobile && { display: "none" }) }}
          >
            <MenuIcon />
          </IconButton>
          <Link to={'/'}>
            <FallbackImage
              src={'/logo.png'}
              style={{ maxHeight: '50px' }}
            />
          </Link>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, paddingLeft: '10px' }}>
            {siteData.name}
          </Typography>
          <ProfileMenu />
        </Toolbar>
      </AppBar>

      {/* ✅ Drawer for Desktop */}
      {!isMobile && <Drawer variant="permanent" open={open}>{drawerContent}</Drawer>}

      {/* ✅ Drawer for Mobile */}
      {isMobile && (
        <MuiDrawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleMobileToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            "& .MuiDrawer-paper": { boxSizing: "border-box", width: drawerWidth },
          }}
        >
          {drawerContent}
        </MuiDrawer>
      )}

      {/* ✅ Main Content */}
      <Box component="main" sx={{ flexGrow: 1, p: 3, backgroundColor: '#ebebeb', minHeight: '100vh' }}>
        <DrawerHeader />
        {user?.id &&
          <>
            {user?.verify_email ? <Outlet /> : <EmailVerificationNotice
              isVerified={user?.verify_email}
              email={user?.email}
            />}
          </>}
      </Box>
    </Box>
  );
}
